/**
 * Authentication Service
 * 
 * API calls related to user authentication
 */

import api from './api';

const authService = {
  /**
   * Validate stored token shape before using it.
   * Avoid treating placeholder strings as real tokens.
   * @returns {string|null} Sanitized token
   */
  getStoredToken: () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return null;
    }

    const normalized = token.trim();
    if (!normalized || normalized === 'undefined' || normalized === 'null') {
      return null;
    }

    return normalized;
  },

  /**
   * Login user with JWT token (OAuth flow)
   * @param {string} token - JWT token from OAuth provider
   * @returns {Promise} User data
   */
  loginWithToken: async (token) => {
    try {
      // Store the token
      localStorage.setItem('authToken', token);
      
      // Fetch user data using the token
      const response = await api.get('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const user = response.data.user;
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(user));
      
      return { token, user };
    } catch (error) {
      // Clear token if fetching user data fails
      localStorage.removeItem('authToken');
      throw error;
    }
  },

  /**
   * Get backend Google OAuth URL
   * @returns {string} Google OAuth URL
   */
  getGoogleAuthUrl: () => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:1337/api';
    return apiUrl.replace('/api', '') + '/api/auth/google';
  },

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Login response with token and user data
   */
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });
      
      // Store token and user data
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise} Registration response
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      
      // Store token and user data
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Request a password reset token for an email address
   * @param {string} email - User email
   * @returns {Promise<Object>} Reset token payload
   */
  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password using token
   * @param {string} token - Password reset token
   * @param {string} password - New password
   * @returns {Promise<Object>} Reset result
   */
  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', {
      token,
      password,
    });
    return response.data;
  },

  /**
   * Change password for authenticated user using current password
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Update result
   */
  changePassword: async (oldPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      oldPassword,
      newPassword,
    });
    return response.data;
  },

  /**
   * Logout user
   * @returns {Promise} Logout response
   */
  logout: async () => {
    try {
      await api.post('/auth/logout');
      
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      
      return { success: true };
    } catch (error) {
      // Clear local storage even if API call fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      throw error;
    }
  },

  /**
   * Get current user profile
   * @returns {Promise} User profile data
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated: () => {
    return !!authService.getStoredToken();
  },

  /**
   * Get stored user data
   * @returns {Object|null} User data from localStorage
   */
  getStoredUser: () => {
    const userStr = localStorage.getItem('user');
    try {
      if (!userStr) {
        return null;
      }

      const parsedUser = JSON.parse(userStr);
      if (!parsedUser || !parsedUser.id) {
        return null;
      }

      return parsedUser;
    } catch (error) {
      return null;
    }
  },
};

export default authService;
